import type {
  AttachmentSource,
  AuthBranding,
  CommandSource,
  DocumentBulkAction,
  DocumentSettingsTab,
  DocumentTab,
  FeedComposerTab,
  GroupTab,
  HiddenDocumentTabs,
  MessengerTab,
  NavItem,
  NotificationRenderer,
  OrbitaModule,
  PageSection,
  ProfileTab,
  SearchResultRenderer,
  SidePanelDefinition,
  TopBarWidget,
} from './types';

const byOrder = <T extends { order: number }>(a: T, b: T) => a.order - b.order;

/**
 * Whether a document type matches a pattern: `*` (everything), `module.*` and `module.type:*` (a prefix) or an exact
 * type — the rules of the backend's `DocumentTypes.Matches`.
 */
export function matchesDocumentType(pattern: string, type: string): boolean {
  if (pattern === '*') return true;
  if (pattern.endsWith('.*') || pattern.endsWith(':*')) return type.startsWith(pattern.slice(0, -1));
  return pattern === type;
}

/** Read-only aggregate of all discovered modules' contributions. */
export class ModuleRegistry {
  readonly modules: readonly OrbitaModule[];
  readonly navItems: readonly NavItem[];
  readonly groupTabs: readonly GroupTab[];
  readonly profileTabs: readonly ProfileTab[];
  readonly topBarWidgets: readonly TopBarWidget[];
  readonly commandSources: readonly CommandSource[];
  readonly attachmentSources: readonly AttachmentSource[];
  readonly messengerTabs: readonly MessengerTab[];
  /** Tabs other modules add to the feed's composer, in order. */
  readonly feedComposerTabs: readonly FeedComposerTab[];
  private readonly sections: readonly PageSection[];
  private readonly docTabs: readonly DocumentTab[];
  private readonly hiddenDocTabs: readonly HiddenDocumentTabs[];
  private readonly docSettingsTabs: readonly DocumentSettingsTab[];
  private readonly docBulkActions: readonly DocumentBulkAction[];
  private readonly sidePanels = new Map<string, SidePanelDefinition>();
  private readonly sidePanelSizes = new Map<string, NonNullable<SidePanelDefinition['size']>>();
  /** The face of the sign-in pages, when a module gives one. */
  readonly authBranding: AuthBranding | undefined;
  private readonly searchRenderers = new Map<string, SearchResultRenderer>();
  private readonly notificationRenderers = new Map<string, NotificationRenderer>();

  constructor(modules: readonly OrbitaModule[]) {
    const ids = new Set<string>();
    for (const module of modules) {
      if (ids.has(module.id)) throw new Error(`Duplicate module id "${module.id}".`);
      ids.add(module.id);
    }
    this.modules = [...modules].sort((a, b) => a.id.localeCompare(b.id));
    this.navItems = modules.flatMap((m) => m.nav ?? []).sort(byOrder);
    this.groupTabs = modules.flatMap((m) => m.groupTabs ?? []).sort(byOrder);
    this.profileTabs = modules.flatMap((m) => m.profileTabs ?? []).sort(byOrder);
    this.topBarWidgets = modules.flatMap((m) => m.topBarWidgets ?? []).sort(byOrder);
    this.commandSources = modules.flatMap((m) => m.commands ?? []).sort(byOrder);
    this.attachmentSources = modules.flatMap((m) => m.attachmentSources ?? []).sort(byOrder);
    this.messengerTabs = modules.flatMap((m) => m.messengerTabs ?? []).sort(byOrder);
    this.feedComposerTabs = modules.flatMap((m) => m.feedComposerTabs ?? []).sort(byOrder);
    this.sections = modules.flatMap((m) => m.pageSections ?? []).sort(byOrder);
    this.docTabs = modules.flatMap((m) => m.documentTabs ?? []).sort(byOrder);
    this.hiddenDocTabs = modules.flatMap((m) => m.hiddenDocumentTabs ?? []);
    this.docSettingsTabs = modules.flatMap((m) => m.documentSettingsTabs ?? []).sort(byOrder);
    this.docBulkActions = modules.flatMap((m) => m.documentBulkActions ?? []).sort(byOrder);

    const branded = modules.filter((m) => m.authBranding !== undefined);
    if (branded.length > 1) throw new Error(`Two modules give the sign-in pages a face: ${branded.map((m) => m.id).join(', ')}.`);
    this.authBranding = branded[0]?.authBranding;

    for (const module of modules) {
      for (const panel of module.sidePanels ?? []) {
        if (this.sidePanels.has(panel.type))
          throw new Error(`Side panel type "${panel.type}" is registered twice.`);
        this.sidePanels.set(panel.type, panel);
      }
      for (const [type, size] of Object.entries(module.sidePanelSizes ?? {})) {
        if (this.sidePanelSizes.has(type)) throw new Error(`Side panel type "${type}" is sized by two modules.`);
        this.sidePanelSizes.set(type, size);
      }
      for (const [entityType, renderer] of Object.entries(module.searchRenderers ?? {})) {
        this.searchRenderers.set(`${module.id}:${entityType}`, renderer);
      }
      for (const [type, renderer] of Object.entries(module.notificationRenderers ?? {})) {
        this.notificationRenderers.set(type, renderer);
      }
    }
  }

  /** Sections other modules add to the page `page` (e.g. `crm`), in order. */
  pageSections(page: string): readonly PageSection[] {
    return this.sections.filter((s) => s.page === page);
  }

  /** Tabs other modules add to the card of a document of `type` (e.g. `crm.deal`), in order. */
  documentTabs(type: string): readonly DocumentTab[] {
    return this.docTabs.filter((tab) =>
      tab.documentTypes.some((pattern) => matchesDocumentType(pattern, type)),
    );
  }

  /** Whether a module took the tab `tabId` off the card of the document type `type` (see `HiddenDocumentTabs`). */
  isDocumentTabHidden(type: string, tabId: string): boolean {
    return this.hiddenDocTabs.some(
      (hidden) =>
        hidden.tabs.includes(tabId) &&
        hidden.documentTypes.some((pattern) => matchesDocumentType(pattern, type)),
    );
  }

  /** Tabs other modules add to the settings of the document type `type`, in order. */
  documentSettingsTabs(type: string): readonly DocumentSettingsTab[] {
    return this.docSettingsTabs.filter((tab) =>
      tab.documentTypes.some((pattern) => matchesDocumentType(pattern, type)),
    );
  }

  /** Actions other modules add to the bulk bar of the list of documents of `type`, in order. */
  documentBulkActions(type: string): readonly DocumentBulkAction[] {
    return this.docBulkActions.filter((action) =>
      action.documentTypes.some((pattern) => matchesDocumentType(pattern, type)),
    );
  }

  getSidePanel(type: string): SidePanelDefinition | undefined {
    return this.sidePanels.get(type);
  }

  /** The width the panel of `type` opens at: what a module asked for through `sidePanelSizes`, else the panel's own `size`. */
  getSidePanelSize(type: string): NonNullable<SidePanelDefinition['size']> | undefined {
    return this.sidePanelSizes.get(type) ?? this.sidePanels.get(type)?.size;
  }

  hasSidePanel(type: string): boolean {
    return this.sidePanels.has(type);
  }

  /** Renderer for a search hit of `entityType` produced by `module`. */
  getSearchRenderer(module: string, entityType: string): SearchResultRenderer | undefined {
    return this.searchRenderers.get(`${module}:${entityType}`);
  }

  /** Renderer for a notification type such as `tasks.assigned`. */
  getNotificationRenderer(type: string): NotificationRenderer | undefined {
    return this.notificationRenderers.get(type);
  }
}
